/**
 * Author: zoesvision.com
 * Purpose: Isolated AJAX filtering, sorting, and pagination for the Z product grid.
 */

(() => {
  let zMobileDrawerOwner = null;
  let zDocumentOverflow = '';

  class ZProductGrid extends HTMLElement {
  constructor() {
    super();
    this.zAbortController = null;
    // zoesvision.com | 2026-08-12 | Purpose: Isolate mobile infinite-loading requests from filter and sort navigation.
    this.zInfiniteAbortController = null;
    this.zInfiniteObserver = null;
    this.zInfiniteLoading = false;
    this.zDrawerAttributes = null;
    this.zDrawerCloseTimer = null;
    this.zIsConnected = false;
    this.zDrawerOpener = null;
    this.zModalBranches = [];
    this.zMobileMedia = window.matchMedia('(max-width: 989px)');
    // zoesvision.com | 2026-08-12 | Purpose: Add isolated custom sort-menu interaction matching Figma.
    this.zHandleClick = this.zHandleClick.bind(this);
    this.zHandleDocumentClick = this.zHandleDocumentClick.bind(this);
    this.zHandleChange = this.zHandleChange.bind(this);
    this.zHandleInput = this.zHandleInput.bind(this);
    this.zHandleDrawerToggle = this.zHandleDrawerToggle.bind(this);
    this.zHandleKeyDown = this.zHandleKeyDown.bind(this);
    this.zHandleSortKeyDown = this.zHandleSortKeyDown.bind(this);
    this.zHandleMediaChange = this.zHandleMediaChange.bind(this);
    this.zHandleSubmit = this.zHandleSubmit.bind(this);
    this.zHandlePopState = this.zHandlePopState.bind(this);
  }

  connectedCallback() {
    if (this.zIsConnected) return;

    this.zIsConnected = true;
    this.zInitializeDrawerState();
    this.zSyncFilterGroups();
    this.addEventListener('click', this.zHandleClick);
    this.addEventListener('change', this.zHandleChange);
    this.addEventListener('input', this.zHandleInput);
    this.addEventListener('submit', this.zHandleSubmit);
    this.addEventListener('toggle', this.zHandleDrawerToggle, true);
    this.addEventListener('keydown', this.zHandleSortKeyDown);
    document.addEventListener('click', this.zHandleDocumentClick);
    window.addEventListener('popstate', this.zHandlePopState);
    this.zMobileMedia.addEventListener('change', this.zHandleMediaChange);
    this.zSetupInfiniteScroll();
  }

  disconnectedCallback() {
    if (!this.zIsConnected) return;

    this.zIsConnected = false;
    this.removeEventListener('click', this.zHandleClick);
    this.removeEventListener('change', this.zHandleChange);
    this.removeEventListener('input', this.zHandleInput);
    this.removeEventListener('submit', this.zHandleSubmit);
    this.removeEventListener('toggle', this.zHandleDrawerToggle, true);
    this.removeEventListener('keydown', this.zHandleSortKeyDown);
    document.removeEventListener('click', this.zHandleDocumentClick);
    window.removeEventListener('popstate', this.zHandlePopState);
    this.zMobileMedia.removeEventListener('change', this.zHandleMediaChange);
    window.clearTimeout(this.zDrawerCloseTimer);
    this.zReleaseMobileDrawer(true);
    this.zAbortController?.abort();
    this.zAbortController = null;
    this.zDisconnectInfiniteScroll();
  }

  zHandleSubmit(event) {
    if (!(event.target instanceof Element)) return;

    const form = event.target.closest('[data-z-sort-form], [data-z-facets-form]');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    const isFilterForm = form.matches('[data-z-facets-form]');
    const recoverDrawerFocus = isFilterForm && this.zShouldRecoverDrawerFocus(form);
    this.zNavigate(this.zUrlFromForm(form, event.submitter), true, false, recoverDrawerFocus, isFilterForm);
  }

  // zoesvision.com | 2026-08-14 | Purpose: Restore the desktop Filter panel after AJAX replaces the component's inner markup.
  zInitializeDrawerState() {
    const drawer = this.querySelector('[data-z-drawer]');
    if (drawer instanceof HTMLDetailsElement) drawer.open = !this.zMobileMedia.matches;
  }

  zHandleChange(event) {
    if (!(event.target instanceof Element)) return;

    const control = event.target.closest(
      '[data-z-facets-form] input[type="checkbox"], [data-z-price-min-range], [data-z-price-max-range]'
    );
    if (!control || !this.contains(control)) return;

    if (control.matches('[data-z-price-min-range], [data-z-price-max-range]')) {
      this.zSyncPriceRange(control);
    }

    // zoesvision.com | 2026-08-12 | Purpose: Let the Figma mobile drawer Apply action submit the selected filters together.
    if (this.zMobileMedia.matches) return;

    const form = control.closest('[data-z-facets-form]');
    if (form) this.zNavigate(this.zUrlFromForm(form), true, false, this.zShouldRecoverDrawerFocus(form), true);
  }

  zHandleInput(event) {
    if (!(event.target instanceof Element)) return;

    const range = event.target.closest('[data-z-price-min-range], [data-z-price-max-range]');
    if (range && this.contains(range)) this.zSyncPriceRange(range);
  }

  zSyncPriceRange(changedRange) {
    const priceRange = changedRange.closest('[data-z-price-range]');
    const minRange = priceRange?.querySelector('[data-z-price-min-range]');
    const maxRange = priceRange?.querySelector('[data-z-price-max-range]');
    if (!priceRange || !minRange || !maxRange) return;

    let minValue = Number(minRange.value);
    let maxValue = Number(maxRange.value);
    const rangeMax = Number(maxRange.max) || 1;

    if (changedRange === minRange && minValue > maxValue) {
      minValue = maxValue;
      minRange.value = String(minValue);
    } else if (changedRange === maxRange && maxValue < minValue) {
      maxValue = minValue;
      maxRange.value = String(maxValue);
    }

    priceRange.style.setProperty('--z-price-start', `${(minValue / rangeMax) * 100}%`);
    priceRange.style.setProperty('--z-price-end', `${(maxValue / rangeMax) * 100}%`);
    minRange.style.zIndex = minValue >= rangeMax ? '4' : '2';
    maxRange.style.zIndex = '3';

    const minInput = priceRange.querySelector('[data-z-price-min-input]');
    const maxInput = priceRange.querySelector('[data-z-price-max-input]');
    if (minInput) minInput.value = (minValue / 100).toFixed(2);
    if (maxInput) maxInput.value = (maxValue / 100).toFixed(2);

    const result = priceRange.querySelector('[data-z-price-result]');
    if (!result) return;

    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: priceRange.dataset.zCurrency
    });
    result.textContent = `${formatter.format(minValue / 100)} - ${formatter.format(maxValue / 100)}`;
  }

  zHandleClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const sortTrigger = event.target.closest('[data-z-sort-trigger]');
    if (sortTrigger && this.contains(sortTrigger)) {
      const dropdown = sortTrigger.closest('[data-z-sort-dropdown]');
      if (dropdown?.classList.contains('is-open')) {
        this.zCloseSortDropdown(dropdown, false);
      } else if (dropdown) {
        this.zOpenSortDropdown(dropdown, false);
      }
      return;
    }

    const sortOption = event.target.closest('[data-z-sort-option]');
    if (sortOption && this.contains(sortOption)) {
      this.zSelectSortOption(sortOption);
      return;
    }

    const drawerOpen = event.target.closest('[data-z-drawer-open]');
    if (drawerOpen && this.contains(drawerOpen)) {
      event.preventDefault();
      const drawer = this.querySelector('[data-z-drawer]');
      if (drawer instanceof HTMLDetailsElement) this.zOpenDrawer(drawer);
      return;
    }

    const loadMore = event.target.closest('[data-z-load-more]');
    if (loadMore && this.contains(loadMore)) {
      event.preventDefault();
      this.zLoadNextPage(loadMore.closest('[data-z-infinite-scroll]'));
      return;
    }

    const drawerClose = event.target.closest('[data-z-drawer-close]');
    if (drawerClose && this.contains(drawerClose)) {
      event.preventDefault();
      this.zCloseDrawer(true);
      return;
    }

    const link = event.target.closest(
      '[data-z-filter-remove], [data-z-clear-filters], .z-product-grid__clear-filter, .z-product-grid-facets__actions a, .pagination-wrapper a[href]'
    );
    if (!link || !this.contains(link) || link.target === '_blank' || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    const isPagination = Boolean(link.closest('.pagination-wrapper'));
    this.zNavigate(url, true, isPagination, this.zShouldRecoverDrawerFocus(link), !isPagination);
  }

  zHandleDocumentClick(event) {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.querySelectorAll('[data-z-sort-dropdown].is-open').forEach((dropdown) => {
      this.zCloseSortDropdown(dropdown, false);
    });
  }

  zHandlePopState() {
    this.zNavigate(new URL(window.location.href), false);
  }

  zHandleDrawerToggle(event) {
    if (!(event.target instanceof HTMLDetailsElement) || !event.target.matches('[data-z-drawer]')) return;

    if (event.target.open && this.zMobileMedia.matches) {
      this.zAcquireMobileDrawer(event.target);
    } else {
      this.zReleaseMobileDrawer(true);
    }
  }

  zHandleMediaChange(event) {
    const drawer = this.querySelector('[data-z-drawer]');
    if (drawer) {
      window.clearTimeout(this.zDrawerCloseTimer);
      this.zDrawerCloseTimer = null;
      drawer.classList.remove('is-open', 'is-closing');
      if (event.matches) {
        if (drawer.open) drawer.open = false;
        this.zReleaseMobileDrawer(false);
      } else {
        drawer.open = true;
        this.zReleaseMobileDrawer(false);
      }
    }

    this.zSyncFilterGroups();
    this.zSetupInfiniteScroll();
  }

  zSyncFilterGroups() {
    const shouldOpen = !this.zMobileMedia.matches;
    this.querySelectorAll('[data-z-filter-group]').forEach((group) => {
      if (group instanceof HTMLDetailsElement) group.open = shouldOpen;
    });
  }

  zHandleSortKeyDown(event) {
    if (event.target instanceof Element) {
      const sortTrigger = event.target.closest('[data-z-sort-trigger]');
      if (sortTrigger && this.contains(sortTrigger)) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          const dropdown = sortTrigger.closest('[data-z-sort-dropdown]');
          if (dropdown) this.zOpenSortDropdown(dropdown, event.key === 'ArrowUp');
        } else if (event.key === 'Escape') {
          this.zCloseSortDropdown(sortTrigger.closest('[data-z-sort-dropdown]'), false);
        }
        return;
      }

      const sortOption = event.target.closest('[data-z-sort-option]');
      if (sortOption && this.contains(sortOption)) {
        const dropdown = sortOption.closest('[data-z-sort-dropdown]');
        const options = Array.from(dropdown?.querySelectorAll('[data-z-sort-option]') || []);
        const index = options.indexOf(sortOption);

        if (event.key === 'Escape') {
          event.preventDefault();
          this.zCloseSortDropdown(dropdown, true);
        } else if (event.key === 'Tab') {
          this.zCloseSortDropdown(dropdown, false);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.zSelectSortOption(sortOption);
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowDown') nextIndex = (index + 1) % options.length;
          if (event.key === 'ArrowUp') nextIndex = (index - 1 + options.length) % options.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = options.length - 1;
          options[nextIndex]?.focus();
        }
        return;
      }
    }
  }

  zHandleKeyDown(event) {
    if (zMobileDrawerOwner !== this) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.zCloseDrawer(true);
      return;
    }

    if (event.key !== 'Tab') return;
    const drawer = this.querySelector('[data-z-drawer]');
    if (!drawer?.open) return;

    const focusable = Array.from(drawer.querySelectorAll(
      'summary, button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!drawer.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  zOpenSortDropdown(dropdown, focusLast) {
    if (!dropdown) return;

    this.querySelectorAll('[data-z-sort-dropdown].is-open').forEach((openDropdown) => {
      if (openDropdown !== dropdown) this.zCloseSortDropdown(openDropdown, false);
    });
    dropdown.classList.add('is-open');
    dropdown.querySelector('[data-z-sort-trigger]')?.setAttribute('aria-expanded', 'true');
    const menu = dropdown.querySelector('[data-z-sort-menu]');
    if (menu) menu.hidden = false;

    const options = Array.from(dropdown.querySelectorAll('[data-z-sort-option]'));
    const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
    (focusLast ? options.at(-1) : selected || options[0])?.focus();
  }

  zCloseSortDropdown(dropdown, restoreFocus) {
    if (!dropdown) return;

    dropdown.classList.remove('is-open');
    const trigger = dropdown.querySelector('[data-z-sort-trigger]');
    trigger?.setAttribute('aria-expanded', 'false');
    const menu = dropdown.querySelector('[data-z-sort-menu]');
    if (menu) menu.hidden = true;
    if (restoreFocus) trigger?.focus();
  }

  zSelectSortOption(option) {
    const dropdown = option.closest('[data-z-sort-dropdown]');
    const form = option.closest('[data-z-sort-form]');
    const input = dropdown?.querySelector('[data-z-sort-input]');
    const label = dropdown?.querySelector('[data-z-sort-label]');
    if (!dropdown || !form || !input || !label) return;

    input.value = option.dataset.zSortValue || '';
    label.textContent = option.textContent.trim();
    dropdown.querySelectorAll('[data-z-sort-option]').forEach((item) => {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    });
    this.zCloseSortDropdown(dropdown, false);
    this.zNavigate(this.zUrlFromForm(form), true);
  }

  zAcquireMobileDrawer(drawer) {
    const summary = drawer.querySelector('[data-z-drawer-summary]');
    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const opener = activeElement && !drawer.contains(activeElement) ? activeElement : summary;
    if (zMobileDrawerOwner && zMobileDrawerOwner !== this) zMobileDrawerOwner.zCloseDrawer(true);
    if (zMobileDrawerOwner === this) return;

    this.zDrawerOpener = opener;
    this.zDrawerAttributes = this.zSaveAttributes(drawer, ['role', 'aria-modal', 'aria-labelledby']);
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    if (summary?.id) drawer.setAttribute('aria-labelledby', summary.id);
    if (!drawer.contains(document.activeElement)) drawer.querySelector('.z-product-grid-facets__close')?.focus();
    this.zHideBackgroundBranches(drawer);
    zMobileDrawerOwner = this;
    zDocumentOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.zHandleKeyDown);

  }

  zReleaseMobileDrawer(restoreFocus) {
    if (zMobileDrawerOwner !== this) return;

    document.removeEventListener('keydown', this.zHandleKeyDown);
    document.documentElement.style.overflow = zDocumentOverflow;
    this.zRestoreBackgroundBranches();

    const drawer = this.querySelector('[data-z-drawer]');
    if (drawer && this.zDrawerAttributes) this.zRestoreAttributes(drawer, this.zDrawerAttributes);
    this.zDrawerAttributes = null;
    zMobileDrawerOwner = null;
    zDocumentOverflow = '';

    const opener = this.zDrawerOpener;
    this.zDrawerOpener = null;
    const openerIsValid = opener?.isConnected && !(drawer && !drawer.open && drawer.contains(opener));
    const focusTarget = openerIsValid ? opener : drawer?.querySelector('[data-z-drawer-summary]');
    if (restoreFocus && focusTarget?.isConnected) focusTarget.focus();
  }

  zSaveAttributes(element, names) {
    return names.map((name) => ({
      name,
      present: element.hasAttribute(name),
      value: element.getAttribute(name)
    }));
  }

  zRestoreAttributes(element, attributes) {
    for (const attribute of attributes) {
      if (attribute.present) {
        element.setAttribute(attribute.name, attribute.value ?? '');
      } else {
        element.removeAttribute(attribute.name);
      }
    }
  }

  zHideBackgroundBranches(drawer) {
    this.zModalBranches = [];
    let branch = drawer;

    while (branch.parentElement) {
      const parent = branch.parentElement;
      for (const sibling of parent.children) {
        if (sibling === branch) continue;
        this.zModalBranches.push({
          element: sibling,
          inertPresent: sibling.hasAttribute('inert'),
          inertValue: sibling.getAttribute('inert'),
          ariaHiddenPresent: sibling.hasAttribute('aria-hidden'),
          ariaHiddenValue: sibling.getAttribute('aria-hidden')
        });
        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
      }

      if (parent === document.body) break;
      branch = parent;
    }
  }

  zRestoreBackgroundBranches() {
    for (const state of this.zModalBranches) {
      if (state.inertPresent) {
        state.element.setAttribute('inert', state.inertValue ?? '');
      } else {
        state.element.removeAttribute('inert');
      }

      if (state.ariaHiddenPresent) {
        state.element.setAttribute('aria-hidden', state.ariaHiddenValue ?? '');
      } else {
        state.element.removeAttribute('aria-hidden');
      }
    }
    this.zModalBranches = [];
  }

  zShouldRecoverDrawerFocus(origin) {
    const drawer = origin.closest('[data-z-drawer]');
    return zMobileDrawerOwner === this && Boolean(drawer?.open && drawer.contains(document.activeElement));
  }

  // zoesvision.com | 2026-08-12 | Purpose: Keep the native details drawer accessible while giving the mobile panel a reversible transition.
  zOpenDrawer(drawer) {
    window.clearTimeout(this.zDrawerCloseTimer);
    this.zDrawerCloseTimer = null;
    drawer.classList.remove('is-closing');
    drawer.open = true;
    requestAnimationFrame(() => {
      if (drawer.open && !drawer.classList.contains('is-closing')) drawer.classList.add('is-open');
    });
  }

  zCloseDrawer(restoreFocus) {
    const drawer = this.querySelector('[data-z-drawer]');
    if (!drawer?.open || drawer.classList.contains('is-closing')) return;

    const finishClose = () => {
      drawer.open = false;
      drawer.classList.remove('is-open', 'is-closing');
      this.zDrawerCloseTimer = null;
      this.zReleaseMobileDrawer(restoreFocus);
    };

    if (!this.zMobileMedia.matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return;
    }

    drawer.classList.remove('is-open');
    drawer.classList.add('is-closing');
    this.zDrawerCloseTimer = window.setTimeout(finishClose, 220);
  }

  zUrlFromForm(form, submitter = null) {
    const url = new URL(form.action || window.location.pathname, window.location.origin);
    url.search = '';
    const validSubmitter = submitter instanceof HTMLElement && submitter.form === form ? submitter : null;
    let formData;

    try {
      formData = validSubmitter ? new FormData(form, validSubmitter) : new FormData(form);
    } catch (error) {
      formData = new FormData(form);
      if (validSubmitter?.name && !validSubmitter.disabled) formData.append(validSubmitter.name, validSubmitter.value);
    }

    for (const [name, value] of formData) {
      const normalizedValue = typeof value === 'string' ? value.trim() : value;
      if (name && normalizedValue !== '') url.searchParams.append(name, normalizedValue);
    }

    return this.zPreserveTemplateView(url);
  }

  // zoesvision.com | 2026-08-14 | Purpose: Keep alternate collection templates active across every filter, sort, and pagination request.
  zPreserveTemplateView(target) {
    const url = target instanceof URL ? new URL(target.toString()) : new URL(target, window.location.href);
    const templateView = this.dataset.zTemplateView || new URL(window.location.href).searchParams.get('view');
    if (templateView && !url.searchParams.has('view')) url.searchParams.set('view', templateView);
    return url;
  }

  zHasCompetingCollectionSurface() {
    const surfaces = document.querySelectorAll(
      'z-product-grid, #ProductGridContainer, .product-grid-container, .main-collection-product-grid-section'
    );
    return Array.from(surfaces).some((surface) => surface !== this && !this.contains(surface));
  }

  async zNavigate(target, pushHistory, focusFragment = false, recoverDrawerFocus = false, scrollToGrid = false) {
    const url = this.zPreserveTemplateView(target);

    // zoesvision.com | 2026-08-14 | Purpose: Keep Theme Editor filtering inside its preview iframe instead of forcing a full navigation to Shopify's 500 page.
    if (!window.Shopify?.designMode && this.zHasCompetingCollectionSurface()) {
      window.location.assign(url.toString());
      return;
    }

    this.zAbortController?.abort();
    this.zDisconnectInfiniteScroll();

    const controller = new AbortController();
    this.zAbortController = controller;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Z product grid request failed: ${response.status}`);

      const documentFromResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sectionId = this.dataset.zSectionId;
      const incomingGrid = Array.from(documentFromResponse.querySelectorAll('z-product-grid[data-z-section-id]'))
        .find((grid) => grid.dataset.zSectionId === sectionId);

      if (!incomingGrid) throw new Error('Matching Z product grid was not found in the response.');
      if (controller.signal.aborted || this.zAbortController !== controller) return;

      this.zReleaseMobileDrawer(false);
      this.innerHTML = incomingGrid.innerHTML;
      this.zInitializeDrawerState();
      this.zSyncFilterGroups();
      this.zSetupInfiniteScroll();
      if (pushHistory && url.toString() !== window.location.href) history.pushState({}, '', url);
      if (recoverDrawerFocus) {
        this.querySelector('[data-z-drawer-summary]')?.focus({ preventScroll: true });
      } else if (focusFragment) {
        this.zFocusFragment(url.hash);
      }
      if (scrollToGrid) this.zScrollToGridTop();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url.toString());
    } finally {
      if (this.zAbortController === controller) {
        this.zAbortController = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  // zoesvision.com | 2026-08-20 | Purpose: Return filtered results to the grid heading without hiding it behind the sticky theme header.
  zScrollToGridTop() {
    const headerVariable = this.zMobileMedia.matches
      ? '--sticky-header-height-mobile'
      : '--sticky-header-height-desktop';
    const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue(headerVariable)) || 0;
    const top = Math.max(0, window.scrollY + this.getBoundingClientRect().top - headerHeight);

    window.scrollTo({
      top,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  zFocusFragment(hash) {
    if (!hash) return;

    let targetId;
    try {
      targetId = decodeURIComponent(hash.slice(1));
    } catch (error) {
      return;
    }

    const target = Array.from(this.querySelectorAll('[id]')).find((element) => element.id === targetId);
    if (!target) return;

    const addedTabIndex = !target.hasAttribute('tabindex');
    if (addedTabIndex) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start', behavior: 'auto' });

    if (addedTabIndex) {
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }
  }

  zDisconnectInfiniteScroll() {
    this.zInfiniteObserver?.disconnect();
    this.zInfiniteObserver = null;
    this.zInfiniteAbortController?.abort();
    this.zInfiniteAbortController = null;
    this.zInfiniteLoading = false;
  }

  zSetupInfiniteScroll() {
    this.zInfiniteObserver?.disconnect();
    this.zInfiniteObserver = null;
    const sentinel = this.querySelector('[data-z-infinite-scroll]');
    if (!this.zMobileMedia.matches || !sentinel || !('IntersectionObserver' in window)) return;

    this.zInfiniteObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.zLoadNextPage(sentinel);
    }, { rootMargin: '300px 0px' });
    this.zInfiniteObserver.observe(sentinel);
  }

  async zLoadNextPage(sentinel) {
    const link = sentinel?.querySelector('[data-z-load-more]');
    if (!sentinel || !link?.href || this.zInfiniteLoading) return;

    this.zInfiniteLoading = true;
    sentinel.classList.add('is-loading');
    sentinel.classList.remove('is-error');
    this.zInfiniteObserver?.disconnect();
    const controller = new AbortController();
    this.zInfiniteAbortController = controller;

    try {
      const nextPageUrl = this.zPreserveTemplateView(link.href);
      const response = await fetch(nextPageUrl.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Z product grid next page failed: ${response.status}`);

      const documentFromResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sectionId = this.dataset.zSectionId;
      const incomingGrid = Array.from(documentFromResponse.querySelectorAll('z-product-grid[data-z-section-id]'))
        .find((grid) => grid.dataset.zSectionId === sectionId);
      const currentList = this.querySelector('[data-z-product-list]');
      const incomingList = incomingGrid?.querySelector('[data-z-product-list]');
      if (!currentList || !incomingList) throw new Error('Matching Z product list was not found in the response.');
      if (controller.signal.aborted || this.zInfiniteAbortController !== controller) return;

      Array.from(incomingList.children).forEach((item) => currentList.append(document.importNode(item, true)));
      // zoesvision.com | 2026-08-16 | Purpose: Observe newly appended infinite-scroll product cards without replaying existing cards.
      this.querySelector('z-animate-on-scroll')?.refresh();
      const nextLink = incomingGrid.querySelector('[data-z-load-more]');
      if (nextLink) {
        link.href = this.zPreserveTemplateView(nextLink.href).toString();
        sentinel.classList.remove('is-loading');
        this.zSetupInfiniteScroll();
      } else {
        sentinel.remove();
      }
    } catch (error) {
      if (error.name !== 'AbortError') sentinel.classList.add('is-error');
    } finally {
      if (this.zInfiniteAbortController === controller) this.zInfiniteAbortController = null;
      this.zInfiniteLoading = false;
      sentinel.classList.remove('is-loading');
    }
  }
}

  if (!customElements.get('z-product-grid')) {
    customElements.define('z-product-grid', ZProductGrid);
  }
})();
